import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Download, ShieldAlert, ShieldCheck, Ban } from "lucide-react";
import { http, formatDetail, openSignedDownload, auDateTime } from "../../../portal/api";

const CATEGORIES = ["Executive briefing", "Governance gap report", "Cultural governance findings", "Recommendations", "Implementation plan", "Assurance summary", "Approved dashboard", "Progress report", "Final report"];
const input = "field-dark w-full px-3 py-2.5 text-sm";
const label = "font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 block mb-1.5";
const emptyMeta = { title: "", category: "", version: "1.0", approved_recipient: "", permitted_purpose: "", licence_conditions: "Organisational internal use for the recorded engagement only. No onward disclosure.", cultural_restrictions: "None recorded", issue_date: "", expiry_date: "", review_date: "" };

export default function OutputsAdminTab({ engagementId, clients, agreementActive }) {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [meta, setMeta] = useState(emptyMeta);
  const [file, setFile] = useState(null);
  const [archive, setArchive] = useState([]);
  const [archiveFile, setArchiveFile] = useState("");
  const [busy, setBusy] = useState(false);
  const [gate, setGate] = useState({});

  const load = useCallback(() => {
    http.get(`/portal/admin/engagements/${engagementId}/outputs`)
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Could not load outputs."));
    http.get("/portal/admin/archive-library")
      .then(({ data }) => setArchive(data))
      .catch(() => {});
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setMeta({ ...meta, [k]: e.target.value });

  const upload = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (archiveFile) {
        await http.post(`/portal/admin/engagements/${engagementId}/outputs/from-archive`, { ...meta, filename: archiveFile });
        toast.success("Archive graphic added as Draft output — complete the release gate to license it.");
      } else {
        if (!file) { toast.error("Attach the output file or choose one from the archive."); setBusy(false); return; }
        const fd = new FormData();
        fd.append("payload", JSON.stringify(meta));
        fd.append("file", file);
        await http.post(`/portal/admin/engagements/${engagementId}/outputs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Output uploaded as Draft.");
      }
      setMeta(emptyMeta); setFile(null); setArchiveFile(""); setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const checkGate = async (id) => {
    try {
      const { data } = await http.get(`/portal/admin/outputs/${id}/release-check`);
      setGate({ ...gate, [id]: data });
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const release = async (id) => {
    try {
      await http.post(`/portal/admin/outputs/${id}/release`);
      toast.success("Output released to the client.");
      setGate({ ...gate, [id]: null });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.missing_conditions) {
        setGate({ ...gate, [id]: { ready: false, missing_conditions: d.missing_conditions } });
        toast.error(d.message);
      } else {
        toast.error(formatDetail(d));
      }
    }
  };

  const revoke = async (id) => {
    try {
      await http.post(`/portal/admin/outputs/${id}/revoke`);
      toast.success("Access revoked.");
      load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-outputs-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-serif text-2xl">Outputs</h2>
        <button onClick={() => setShowForm(!showForm)} data-testid="toggle-output-form-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300">
          {showForm ? "Close" : "Upload approved output"}
        </button>
      </div>
      {!agreementActive && (
        <p className="text-sm text-ochre border border-ochre/50 bg-surface1 p-4 mb-6" data-testid="agreement-warning">
          The engagement agreement is not marked active — release will be blocked until it is (Overview tab).
        </p>
      )}

      {showForm && (
        <form onSubmit={upload} className="border border-line bg-surface1 p-6 md:p-8 mb-8" data-testid="output-upload-form">
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2"><label className={label}>Title</label><input data-testid="out-title" className={input} value={meta.title} onChange={set("title")} /></div>
            <div><label className={label}>Category</label>
              <select data-testid="out-category" className={input} value={meta.category} onChange={set("category")}>
                <option value="">Select…</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><label className={label}>Version</label><input data-testid="out-version" className={input} value={meta.version} onChange={set("version")} /></div>
            <div className="md:col-span-2"><label className={label}>Approved recipient (verified client)</label>
              <select data-testid="out-recipient" className={input} value={meta.approved_recipient} onChange={set("approved_recipient")}>
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.email} value={c.email}>{c.name} — {c.email} ({c.status})</option>)}
              </select>
            </div>
            <div className="md:col-span-3"><label className={label}>Permitted purpose</label><input data-testid="out-purpose" className={input} value={meta.permitted_purpose} onChange={set("permitted_purpose")} /></div>
            <div className="md:col-span-2"><label className={label}>Licence conditions</label><input data-testid="out-licence" className={input} value={meta.licence_conditions} onChange={set("licence_conditions")} /></div>
            <div><label className={label}>Cultural restrictions</label><input data-testid="out-cultural" className={input} value={meta.cultural_restrictions} onChange={set("cultural_restrictions")} /></div>
            <div><label className={label}>Issue date</label><input type="date" data-testid="out-issue" className={input} value={meta.issue_date} onChange={set("issue_date")} /></div>
            <div><label className={label}>Expiry date (optional)</label><input type="date" data-testid="out-expiry" className={input} value={meta.expiry_date} onChange={set("expiry_date")} /></div>
            <div><label className={label}>Review date (optional)</label><input type="date" data-testid="out-review" className={input} value={meta.review_date} onChange={set("review_date")} /></div>
          </div>
          {archive.length > 0 && (
            <div className="border border-ochre/50 bg-ink p-4 mb-5" data-testid="archive-picker">
              <label className={label}>Release from the protected archive (licensed & watermarked at download — no re-upload needed)</label>
              <select data-testid="archive-file-select" className={input} value={archiveFile} onChange={(e) => { setArchiveFile(e.target.value); if (e.target.value) setFile(null); }}>
                <option value="">— Upload a file instead —</option>
                {archive.map((a) => <option key={a.filename} value={a.filename}>{a.filename} ({Math.round(a.size / 1024)} KB)</option>)}
              </select>
            </div>
          )}
          {!archiveFile && (
            <>
              <label className={label}>Output file (PDF and image outputs are stamped with the recipient watermark at download)</label>
              <input type="file" data-testid="out-file" className="block text-sm text-stone2 file:mr-4 file:font-mono file:text-xs file:uppercase file:bg-ember file:text-ink file:border-0 file:px-4 file:py-2 file:cursor-pointer mb-5" onChange={(e) => setFile(e.target.files?.[0] || null)} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg" />
            </>
          )}
          <button type="submit" disabled={busy} data-testid="upload-output-btn" className="font-mono text-sm uppercase tracking-[0.2em] px-8 py-3.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-60">
            {busy ? "Saving…" : archiveFile ? "Add archive graphic as draft" : "Upload as draft"}
          </button>
        </form>
      )}

      {items === null ? (
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone2 border border-line bg-surface1 p-8" data-testid="admin-outputs-empty">No outputs uploaded yet.</p>
      ) : (
        <div className="space-y-px bg-line/40 border border-line/40">
          {items.map((o, i) => (
            <article key={o.id} data-testid={`admin-output-row-${i}`} className="bg-ink p-5 md:p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <div className="flex flex-wrap items-baseline gap-3">
                  <h3 className="font-serif text-lg">{o.title}</h3>
                  <span className="font-mono text-xs text-stone2">{o.document_id} · v{o.version}</span>
                </div>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${o.status === "Released" ? "border-ember/50 text-ember" : o.status === "Revoked" ? "border-line text-stone2 line-through" : "border-ochre text-ochre"}`} data-testid={`admin-output-status-${i}`}>{o.status}</span>
              </div>
              <div className="flex flex-wrap gap-2 font-mono text-[10px] text-stone2 mb-3">
                <span className="px-2 py-0.5 border border-line">{o.category}</span>
                <span className="px-2 py-0.5 border border-line">Recipient: {o.approved_recipient}</span>
                <span className="px-2 py-0.5 border border-line">{o.watermark_id}</span>
                {o.expiry_date && <span className="px-2 py-0.5 border border-line">Expires {o.expiry_date}</span>}
                <span className="px-2 py-0.5 border border-line">{auDateTime(o.created_at)}</span>
              </div>
              {gate[o.id] && !gate[o.id].ready && (
                <div className="border border-ochre/60 bg-surface1 p-4 mb-3" data-testid={`gate-blocked-${i}`}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ochre mb-2 flex items-center gap-2"><ShieldAlert size={13} /> Release blocked — governance conditions incomplete.</p>
                  {gate[o.id].missing_conditions.map((m) => <p key={m} className="text-sm text-stone2">— {m}</p>)}
                </div>
              )}
              {gate[o.id]?.ready && o.status !== "Released" && (
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-ember mb-3 flex items-center gap-2" data-testid={`gate-ready-${i}`}><ShieldCheck size={13} /> All governance conditions met — ready to release.</p>
              )}
              <div className="flex flex-wrap gap-3">
                {o.status !== "Released" && o.status !== "Revoked" && (
                  <>
                    <button onClick={() => checkGate(o.id)} data-testid={`check-gate-${i}`} className="font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 border border-line text-stone2 hover:border-ember hover:text-ember transition-colors duration-300">Check release gate</button>
                    <button onClick={() => release(o.id)} data-testid={`release-output-${i}`} className="font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300">Approve release</button>
                  </>
                )}
                {o.status === "Released" && (
                  <button onClick={() => revoke(o.id)} data-testid={`revoke-output-${i}`} className="font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 border border-line text-stone2 hover:border-ember hover:text-ember transition-colors duration-300 flex items-center gap-1.5">
                    <Ban size={12} /> Revoke access
                  </button>
                )}
                <button onClick={() => openSignedDownload(`/portal/admin/outputs/${o.id}/download-link`).catch((err) => toast.error(formatDetail(err.response?.data?.detail)))} data-testid={`admin-output-download-${i}`} className="font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 border border-line text-stone2 hover:border-ember hover:text-ember transition-colors duration-300 flex items-center gap-1.5">
                  <Download size={12} /> File
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
